import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Download } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import { formatDateTime } from '../../utils/datetime';
import Button from './Button';
import Spinner from './Spinner';
import CopyButton from './CopyButton';
import Tabs from './Tabs';

// The body of ONE operation, opened from its row in the Operations table.
//
// The same document the module's Payload tab shows when this operation happens to be the designated
// message — there is one row per operation path, and "the message" is a flag on one of them rather than
// a separate thing. So opening `GET /hotels/{hotelId}/activities` here shows that call's body, not the
// module's representative one, which is the distinction the Operations table exists to make.
export default function UapiOperationPayload({ operation, routePath, onClose }) {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [view, setView] = useState('schema');
    const [importing, setImporting] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.getOperationPayload(operation.id);
            setPayload(res.data || res);
        } catch (e) {
            // 404 is the ordinary answer here, not a fault: the spec may not publish a body for this
            // path, or nobody has imported it yet. Said as such rather than as a failure.
            if (e.response?.status === 404) setPayload(null);
            else setError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Could not load that payload');
        } finally {
            setLoading(false);
        }
    }, [operation.id]);

    useEffect(() => { load(); }, [load]);

    // Import just this operation's body, from here.
    //
    // The module's Payload tab can do it for everything at once, but a reader who opened one operation
    // and found nothing should not have to leave, find the tab, and pick this path out of a list of
    // fifty-eight to answer the question they already asked.
    //
    // `route_id` is the operation's own route — usually a version beneath the module (/act/v1). The
    // lambda resolves that up to /act, whose spec this comes from, so nothing here has to walk the tree.
    const importThis = async () => {
        setImporting(true);
        setError(null);
        try {
            await legacyUapiService.importRoutePayloads(
                operation.route_id, undefined, [`${routePath}${operation.path}`]);
            await load();
        } catch (e) {
            const msg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
            // The ordinary miss: the spec publishes no body for this path — a POST-only search, say —
            // so there is nothing to import and no amount of retrying changes that.
            setError(/publishes none/i.test(msg || '')
                ? `Oracle's spec publishes no body for ${routePath}${operation.path}. `
                  + 'Only operations with a documented response can be imported.'
                : (msg || 'Import failed'));
        } finally {
            setImporting(false);
        }
    };

    const pretty = (text) => {
        try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
    };

    const shown = payload && (view === 'sample' ? payload.sample_json : payload.schema_json);

    return (
        <div className="lu-import-backdrop" onClick={onClose}>
            <div className="lu-import-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lu-import-head">
                    <h3 className="lu-import-title">
                        <span className={`lu-method lu-method-${String(operation.method).toLowerCase()}`}>
                            {operation.method}
                        </span>
                        {' '}
                        <span className="lu-mono">{routePath}{operation.path}</span>
                    </h3>
                    <p className="lu-import-sub">
                        {operation.description || 'The body this operation carries, as JSON Schema.'}
                        {payload?.is_message === 'Y' && ' This payload is also the module’s message.'}
                    </p>
                </div>

                <div className="lu-import-list">
                    {loading ? (
                        <div className="loading-section"><Spinner size="md" /><p>Loading payload...</p></div>
                    ) : error ? (
                        <div className="error-alert">
                            <div className="error-alert-content">
                                <AlertCircle className="error-alert-icon" />
                                <p className="error-alert-message">{error}</p>
                            </div>
                        </div>
                    ) : !payload ? (
                        <div className="lu-payload-empty">
                            <p className="lu-empty" style={{ margin: 0 }}>
                                No payload imported for this operation. Oracle publishes one spec per module,
                                and every operation’s body comes from it.
                            </p>
                            <Button variant="secondary" onClick={importThis} disabled={importing}>
                                <Download className="icon" />
                                {importing ? 'Importing…' : 'Import this operation'}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="lu-payload-doc-bar">
                                <Tabs
                                    size="sm"
                                    className="lu-doc-tabs"
                                    active={view}
                                    onChange={setView}
                                    tabs={[
                                        { key: 'schema', label: 'JSON Schema' },
                                        { key: 'sample', label: 'Example', hidden: !payload.sample_json },
                                    ]}
                                />
                                <div className="lu-payload-doc-actions">
                                    <a className="lu-payload-source" href={payload.spec_url} target="_blank" rel="noreferrer"
                                       title={`imported ${formatDateTime(payload.imported_at)}`}>
                                        {payload.spec_file}{payload.spec_version ? ` · ${payload.spec_version}` : ''}
                                    </a>
                                    <CopyButton value={pretty(shown)} label="Copy" className="copy-btn-inline" />
                                </div>
                            </div>
                            <pre className="lu-payload-json">{pretty(shown)}</pre>
                            {/* Which methods this one schema serves. OHIP describes a resource once, so a
                                GET body is also what a POST or PUT of it sends. */}
                            {payload.applies_to && (
                                <p className="lu-payload-hint" style={{ padding: '0 1rem 0.75rem' }}>
                                    Serves {payload.applies_to.split(',').join(', ')} on {payload.operation_path}.
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="lu-import-footer">
                    <button type="button" className="pp-criteria-add-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}
